import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateAfterCompanyMutation } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError, mapFieldErrors, firstFieldError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { companyApi } from 'features/company/company.api';
import { useCompany } from 'features/company/CompanyContext';
import { Card, PageHeader, Skeleton } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { TextInput } from 'shared/components/Input';
import { Button } from 'shared/components/Button';

export function CompanySettingsPage() {
  const { t } = useI18n();
  const { company, companyId, permissions } = useCompany();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    legal_name: company?.legal_name || '',
    display_name: company?.display_name || '',
    registration_number: company?.registration_number || '',
    contact_email: company?.contact_email || '',
    contact_phone: company?.contact_phone || '',
    contact_address: company?.contact_address || '',
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const membersQuery = useQuery({
    queryKey: queryKeys.companyMembers(1),
    queryFn: () => companyApi.members(companyId, { page: 1, per_page: 50 }),
    enabled: permissions.canManageMembers,
  });

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!permissions.canUpdateCompany || submitting) {
      return;
    }
    setSubmitting(true);
    setMessage('');
    try {
      await companyApi.updateCompany(companyId, form);
      await invalidateAfterCompanyMutation(queryClient);
      setMessage(t('company.settings.saved'));
    } catch (error) {
      setErrors(mapFieldErrors(error.errors || {}));
      setMessage(humanizeApiError(error, t));
    } finally {
      setSubmitting(false);
    }
  };

  if (!company) {
    return <Skeleton rows={6} />;
  }

  const members = membersQuery.data?.items || [];
  return (
    <div className="company-settings-page">
      <PageMeta titleKey="meta.companySettings" />
      <PageHeader title={t('company.settings.title')} description={t('company.settings.body')} />
      {permissions.canUpdateCompany ? (
        <Card as="form" onSubmit={(event) => { void handleSubmit(event); }}>
          <TextInput label={t('company.onboarding.legalName')} value={form.legal_name} onChange={update('legal_name')} error={firstFieldError(errors, 'legal_name')} required />
          <TextInput label={t('company.onboarding.displayName')} value={form.display_name} onChange={update('display_name')} error={firstFieldError(errors, 'display_name')} />
          <TextInput label={t('company.onboarding.registrationNumber')} value={form.registration_number} onChange={update('registration_number')} error={firstFieldError(errors, 'registration_number')} />
          <TextInput label={t('company.onboarding.contactEmail')} value={form.contact_email} onChange={update('contact_email')} error={firstFieldError(errors, 'contact_email')} />
          <TextInput label={t('company.onboarding.contactPhone')} value={form.contact_phone} onChange={update('contact_phone')} error={firstFieldError(errors, 'contact_phone')} />
          <TextInput label={t('company.onboarding.contactAddress')} value={form.contact_address} onChange={update('contact_address')} error={firstFieldError(errors, 'contact_address')} />
          {message ? <p role="status">{message}</p> : null}
          <Button type="submit" loading={submitting}>{t('actions.save')}</Button>
        </Card>
      ) : null}
      {permissions.canManageMembers ? (
        <Card>
          <h2>{t('company.settings.members')}</h2>
          <div className="record-table">
            {members.map((membership) => (
              <article key={membership.id} className="record-row">
                <strong>{membership.user?.name || membership.user_name}</strong>
                <p>{membership.role_label || membership.role}</p>
              </article>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
